import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, Button, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Switch, FormControlLabel, Chip, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { adminApi } from '../../api';

const EMPTY = {
  name: '', display_name: '', backend_type: 'filesystem', base_uri: '',
  storage_tier: 'hot', is_default: false, enabled: true, description: '',
};
const TYPES = ['filesystem', 's3', 'azure', 'gcs', 'dicomweb'];
const TIERS = ['hot', 'cool', 'archive'];

const pill = (label, color, bg) => <Chip label={label} size="small" sx={{ height: 18, fontSize: 10, borderRadius: 0, color, bgcolor: bg }} />;

const StorageBackends = () => {
  const [backends, setBackends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => adminApi.getStorageBackends().then(setBackends).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const close = () => setDialog(null);
  const upd = (key, value) => setDialog(d => ({ ...d, data: { ...d.data, [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...dialog.data };
      if (dialog.mode === 'create') await adminApi.createStorageBackend(payload);
      else await adminApi.updateStorageBackend(payload.id, payload);
      close(); await load();
    } catch (e) { console.error(e); }
    setSaving(false);
  };
  const del = async (id) => {
    if (!window.confirm('Delete this storage backend? Assigned facilities will fall back to default.')) return;
    await adminApi.deleteStorageBackend(id);
    load();
  };

  const field = (key, label, opts = {}) => (
    <TextField key={key} label={label} size="small" fullWidth value={dialog?.data?.[key] ?? ''}
      onChange={e => upd(key, e.target.value)}
      sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 0 }, '& .MuiInputLabel-root': { color: '#666' }, '& input': { color: '#e0e0e0' }, '& textarea': { color: '#e0e0e0' } }}
      {...opts}
    />
  );

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography sx={{ color: 'primary.main', fontSize: 20, fontWeight: 700, letterSpacing: 1 }}>Storage Backends</Typography>
          <Typography sx={{ color: '#555', fontSize: 12 }}>Define storage locations and assign them per facility from the Facilities tab.</Typography>
        </Box>
        <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={() => setDialog({ mode: 'create', data: { ...EMPTY } })}
          sx={{ borderRadius: 0, borderColor: '#333', color: '#aaa', '&:hover': { borderColor: 'primary.main', color: 'primary.main' } }}>
          Add Backend
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ bgcolor: '#111', border: '1px solid #1e1e1e', borderRadius: 1 }}>
        <Table size="small">
          <TableHead><TableRow>{['Name','Type','Tier','URI','Default','Status',''].map(h => (
            <TableCell key={h} sx={{ bgcolor: '#0d0d0d', color: 'primary.main', fontSize: 11, fontWeight: 700, letterSpacing: 0.8, borderBottom: '1px solid #222' }}>{h.toUpperCase()}</TableCell>
          ))}</TableRow></TableHead>
          <TableBody>{backends.map(b => (
            <TableRow key={b.id} sx={{ '&:hover': { bgcolor: '#141414' } }}>
              <TableCell sx={{ color: '#e0e0e0', fontSize: 13, fontFamily: 'monospace', borderBottom: '1px solid #1a1a1a' }}>{b.display_name || b.name}<Typography sx={{ color: '#555', fontSize: 11 }}>{b.name}</Typography></TableCell>
              <TableCell sx={{ color: '#aaa', fontSize: 12, borderBottom: '1px solid #1a1a1a' }}>{b.backend_type}</TableCell>
              <TableCell sx={{ borderBottom: '1px solid #1a1a1a' }}>{pill(b.storage_tier, b.storage_tier === 'hot' ? '#00bcd4' : b.storage_tier === 'cool' ? '#7986cb' : '#ff8a65', b.storage_tier === 'hot' ? '#002233' : b.storage_tier === 'cool' ? '#1a1a2e' : '#2e1a0e')}</TableCell>
              <TableCell sx={{ color: '#aaa', fontSize: 12, borderBottom: '1px solid #1a1a1a', fontFamily: 'monospace' }}>{b.base_uri}</TableCell>
              <TableCell sx={{ borderBottom: '1px solid #1a1a1a' }}>{b.is_default ? pill('DEFAULT', '#66bb6a', '#003322') : '—'}</TableCell>
              <TableCell sx={{ borderBottom: '1px solid #1a1a1a' }}>{b.enabled ? pill('ACTIVE', '#00bcd4', '#002233') : pill('DISABLED', '#555', '#1a1a1a')}</TableCell>
              <TableCell sx={{ borderBottom: '1px solid #1a1a1a' }}>
                <Tooltip title="Edit"><IconButton size="small" onClick={() => setDialog({ mode: 'edit', data: { ...b } })} sx={{ color: '#555', '&:hover': { color: 'primary.main' } }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                {!b.is_default && <Tooltip title="Delete"><IconButton size="small" onClick={() => del(b.id)} sx={{ color: '#555', '&:hover': { color: '#ef9a9a' } }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
              </TableCell>
            </TableRow>
          ))}</TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!dialog} onClose={close} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: '#111', border: '1px solid #222', borderRadius: 1 } }}>
        <DialogTitle sx={{ color: 'primary.main', fontSize: 14, letterSpacing: 1 }}>{dialog?.mode === 'create' ? 'ADD STORAGE BACKEND' : 'EDIT STORAGE BACKEND'}</DialogTitle>
        <DialogContent><Box sx={{ pt: 1 }}>
          {field('name', 'Backend Key', { disabled: dialog?.mode === 'edit' })}
          {field('display_name', 'Display Name')}
          <FormControl fullWidth size="small" sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 0 }, '& .MuiInputLabel-root': { color: '#666' }, '& .MuiSelect-select': { color: '#e0e0e0' } }}>
            <InputLabel>Backend Type</InputLabel><Select value={dialog?.data?.backend_type || 'filesystem'} label="Backend Type" onChange={e => upd('backend_type', e.target.value)}>{TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}</Select>
          </FormControl>
          {field('base_uri', 'Base URI / Path')}
          <FormControl fullWidth size="small" sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 0 }, '& .MuiInputLabel-root': { color: '#666' }, '& .MuiSelect-select': { color: '#e0e0e0' } }}>
            <InputLabel>Storage Tier</InputLabel><Select value={dialog?.data?.storage_tier || 'hot'} label="Storage Tier" onChange={e => upd('storage_tier', e.target.value)}>{TIERS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}</Select>
          </FormControl>
          {field('description', 'Description', { multiline: true, minRows: 2 })}
          <FormControlLabel control={<Switch checked={dialog?.data?.is_default || false} onChange={e => upd('is_default', e.target.checked)} size="small" />} label={<Typography sx={{ color: '#aaa', fontSize: 13 }}>Default Backend</Typography>} sx={{ mb: 1 }} />
          <FormControlLabel control={<Switch checked={dialog?.data?.enabled ?? true} onChange={e => upd('enabled', e.target.checked)} size="small" />} label={<Typography sx={{ color: '#aaa', fontSize: 13 }}>Enabled</Typography>} />
        </Box></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={close} sx={{ color: '#555' }}>Cancel</Button><Button onClick={save} disabled={saving} variant="outlined" sx={{ borderRadius: 0, borderColor: 'primary.main', color: 'primary.main' }}>{saving ? 'Saving…' : 'Save'}</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default StorageBackends;
